import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { personalityQuestions, traitQuestions, visualAttributeQuestions } from '../data/mock';
import { ChevronLeft, ChevronRight, Check, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import SectionSeparator from '../components/SectionSeparator';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { analytics } from '../utils/analytics';

const Quiz = () => {
  const navigate = useNavigate();
  const [section, setSection] = useState(1); // 1 = personality, 1.5 = separator, 2 = traits, 2.5 = separator, 3 = visual, 4 = email
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [personalityAnswers, setPersonalityAnswers] = useState([]);
  const [traitAnswers, setTraitAnswers] = useState([]);
  const [visualAnswers, setVisualAnswers] = useState([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showSeparator, setShowSeparator] = useState(false);
  const [separatorConfig, setSeparatorConfig] = useState({});
  
  // OTP verification states
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track quiz start on mount + abandonment on unmount
  const isSubmittingRef = React.useRef(false);
  useEffect(() => {
    analytics.quizStart();
    return () => {
      if (!isSubmittingRef.current) {
        analytics.quizAbandoned(section, currentQuestion);
      }
    };
  }, []); // mount/unmount only

  const totalPersonalityQuestions = personalityQuestions.length;
  const totalTraitQuestions = traitQuestions.length;
  const maxVisualQuestions = 3;

  const handlePersonalityAnswer = (side) => {
    const newAnswers = [...personalityAnswers, side];
    setPersonalityAnswers(newAnswers);
    analytics.quizQuestionAnswer(1, currentQuestion);

    if (currentQuestion < totalPersonalityQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Show separator before Section 2
      setShowSeparator(true);
      setSeparatorConfig({
        sectionNumber: 2,
        title: "Section 2: Choose Traits",
        description: "Now let's explore deeper characteristics that define your brand's personality. These 25 questions will help us pinpoint your brand archetype with precision.",
        nextSection: 2
      });
      analytics.quizSectionComplete(1, totalPersonalityQuestions);
    }
  };

  const handleTraitAnswer = (side) => {
    const newAnswers = [...traitAnswers, side];
    setTraitAnswers(newAnswers);
    analytics.quizQuestionAnswer(2, currentQuestion);

    if (currentQuestion < totalTraitQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Show separator before Section 3
      setShowSeparator(true);
      setSeparatorConfig({
        sectionNumber: 3,
        title: "Section 3: Visual Preferences",
        description: "Finally, let's define your brand's visual identity. Select up to 3 visual attributes that resonate with your brand's aesthetic.",
        nextSection: 3
      });
      analytics.quizSectionComplete(2, totalTraitQuestions);
    }
  };

  const handleContinueFromSeparator = () => {
    setShowSeparator(false);
    setSection(separatorConfig.nextSection);
    setCurrentQuestion(0);
  };

  const handleVisualAnswer = (questionId, option) => {
    // Check if this question was already answered
    const existingIndex = visualAnswers.findIndex(a => a.questionId === questionId);
    
    if (existingIndex >= 0) {
      // If same option clicked again, remove it (deselect)
      if (visualAnswers[existingIndex].answer === option) {
        const newAnswers = visualAnswers.filter(a => a.questionId !== questionId);
        setVisualAnswers(newAnswers);
      } else {
        // Replace with new option
        const newAnswers = [...visualAnswers];
        newAnswers[existingIndex] = { questionId, answer: option };
        setVisualAnswers(newAnswers);
      }
    } else {
      // Add new answer
      if (visualAnswers.length < maxVisualQuestions) {
        setVisualAnswers([...visualAnswers, { questionId, answer: option }]);
      }
    }
  };

  const isQuestionAnswered = (questionId) => {
    return visualAnswers.find(a => a.questionId === questionId);
  };

  const isOptionSelected = (questionId, option) => {
    const answer = visualAnswers.find(a => a.questionId === questionId);
    return answer && answer.answer === option;
  };

  const proceedToEmailSection = () => {
    analytics.quizSectionComplete(3, visualAnswers.length);
    setSection(4);
  };

  const handleSendOtp = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSendingOtp(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setOtpSent(true);
        toast.success('OTP sent to your email!');
        if (data.dev_mode) {
          toast.info('Dev mode: Check server logs for OTP', { duration: 5000 });
        }
      } else {
        toast.error(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    
    setIsVerifyingOtp(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otpCode })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsVerified(true);
        toast.success('Email verified successfully!');
      } else {
        toast.error(data.message || 'Invalid OTP');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error('Failed to verify OTP. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpCode('');
    await handleSendOtp();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !name || !companyName) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (!isVerified) {
      toast.error('Please verify your email first');
      return;
    }

    setIsSubmitting(true);
    isSubmittingRef.current = true;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/submit-quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          personality_answers: personalityAnswers,
          trait_answers: traitAnswers,
          visual_answers: visualAnswers,
          user_data: { name, email, company_name: companyName }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit quiz');
      }

      const data = await response.json();

      analytics.quizSubmit(data.results?.dominant, data.results?.secondary);

      // Navigate to results page with backend-calculated results
      navigate('/results', {
        state: {
          results: data.results,
          email,
          name,
          companyName,
          visualAnswers,
          visualVibes: data.visual_vibes,
          submissionId: data.submission_id
        }
      });
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProgress = () => {
    if (section === 1) {
      return (personalityAnswers.length / totalPersonalityQuestions) * 25;
    } else if (section === 2) {
      return 25 + (traitAnswers.length / totalTraitQuestions) * 25;
    } else if (section === 3) {
      return 50 + (visualAnswers.length / maxVisualQuestions) * 25;
    } else {
      return 75;
    }
  };

  const goBack = () => {
    if (section === 1 && currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setPersonalityAnswers(personalityAnswers.slice(0, -1));
    } else if (section === 2 && currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setTraitAnswers(traitAnswers.slice(0, -1));
    } else if (section === 2 && currentQuestion === 0) {
      setSection(1);
      setCurrentQuestion(totalPersonalityQuestions - 1);
      setPersonalityAnswers(personalityAnswers.slice(0, -1));
    } else if (section === 3) {
      setSection(2);
      setCurrentQuestion(totalTraitQuestions - 1);
      setTraitAnswers(traitAnswers.slice(0, -1));
    } else if (section === 4) {
      setSection(3);
    }
  };

  // Show separator screen
  if (showSeparator) {
    return (
      <SectionSeparator
        sectionNumber={separatorConfig.sectionNumber}
        title={separatorConfig.title}
        description={separatorConfig.description}
        onContinue={handleContinueFromSeparator}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-black">
      <Header />
      
      {/* Quiz Progress Bar */}
      <div className="bg-[#0a0a0a] border-b border-[#262626]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-[#d4d4d4] hover:text-[#f5f5f5] p-0 h-auto">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Button>
            <div className="text-sm font-medium text-[#a3a3a3]">
              {section === 1 && `Section 1 - Question ${currentQuestion + 1} of ${totalPersonalityQuestions}`}
              {section === 2 && `Section 2 - Question ${currentQuestion + 1} of ${totalTraitQuestions}`}
              {section === 3 && `Section 3 - Visual Attributes (${visualAnswers.length}/${maxVisualQuestions} selected)`}
              {section === 4 && 'Final Step - Your Details'}
            </div>
          </div>
          <Progress value={getProgress()} className="h-2" />
        </div>
      </div>

      <main className="flex-1 bg-black pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 md:py-12">
        {/* Section 1: Personality Questions */}
        {section === 1 && (
          <div className="p-2 sm:p-4">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 text-purple-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                Section 1: Personality Traits
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#f5f5f5] mb-3 sm:mb-4 px-2">
                Which trait better describes your brand?
              </h2>
              <p className="text-sm sm:text-base text-[#a3a3a3] px-2">Choose the option that resonates most with your brand's personality</p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-6 sm:mb-8" key={`personality-${currentQuestion}`}>
              <button
                onClick={() => handlePersonalityAnswer('left')}
                className="w-full sm:flex-1 sm:max-w-xs group focus:outline-none touch-manipulation"
                type="button"
              >
                <div className="bg-[#0a0a0a] border-2 border-[#e5e5e5] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 hover:shadow-xl sm:hover:bg-gray-900 sm:hover:text-white transition-colors duration-150 sm:hover:scale-105">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold">
                    {personalityQuestions[currentQuestion].leftTrait}
                  </div>
                </div>
              </button>

              <div className="text-lg sm:text-2xl font-light text-gray-400 py-2 sm:py-0">or</div>

              <button
                onClick={() => handlePersonalityAnswer('right')}
                className="w-full sm:flex-1 sm:max-w-xs group focus:outline-none touch-manipulation"
                type="button"
              >
                <div className="bg-[#0a0a0a] border-2 border-[#e5e5e5] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 hover:shadow-xl sm:hover:bg-gray-900 sm:hover:text-white transition-colors duration-150 sm:hover:scale-105">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold">
                    {personalityQuestions[currentQuestion].rightTrait}
                  </div>
                </div>
              </button>
            </div>

            {currentQuestion > 0 && (
              <div className="flex justify-center">
                <Button variant="outline" onClick={goBack}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous Question
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Section 2: Trait Questions */}
        {section === 2 && (
          <div className="p-2 sm:p-4">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 text-purple-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                Section 2: Choose Traits
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#f5f5f5] mb-3 sm:mb-4 px-2">
                Which trait better describes your brand?
              </h2>
              <p className="text-sm sm:text-base text-[#a3a3a3] px-2">Choose the option that resonates most with your brand's characteristics</p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-6 sm:mb-8" key={`trait-${currentQuestion}`}>
              <button
                onClick={() => handleTraitAnswer('left')}
                className="w-full sm:flex-1 sm:max-w-xs group focus:outline-none touch-manipulation"
                type="button"
              >
                <div className="bg-[#0a0a0a] border-2 border-[#e5e5e5] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 hover:shadow-xl sm:hover:bg-gray-900 sm:hover:text-white transition-colors duration-150 sm:hover:scale-105">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold">
                    {traitQuestions[currentQuestion].leftTrait}
                  </div>
                </div>
              </button>

              <div className="text-lg sm:text-2xl font-light text-gray-400 py-2 sm:py-0">or</div>

              <button
                onClick={() => handleTraitAnswer('right')}
                className="w-full sm:flex-1 sm:max-w-xs group focus:outline-none touch-manipulation"
                type="button"
              >
                <div className="bg-[#0a0a0a] border-2 border-[#e5e5e5] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 hover:shadow-xl sm:hover:bg-gray-900 sm:hover:text-white transition-colors duration-150 sm:hover:scale-105">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold">
                    {traitQuestions[currentQuestion].rightTrait}
                  </div>
                </div>
              </button>
            </div>

            {currentQuestion > 0 && (
              <div className="flex justify-center">
                <Button variant="outline" onClick={goBack}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous Question
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Section 3: Visual Attributes */}
        {section === 3 && (
          <div className="p-2 sm:p-4">
            {/* Section Header with Clear Instructions */}
            <div className="text-center mb-6 sm:mb-8 md:mb-10 pb-6 sm:pb-8 border-b-2 sm:border-b-4 border-teal-200">
              <div className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-teal-100 text-teal-700 rounded-full text-xs sm:text-sm md:text-base font-bold mb-4 sm:mb-6">
                SECTION 3: VISUAL ATTRIBUTES
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#f5f5f5] mb-4 sm:mb-6 px-2">
                Select Your Visual Preferences
              </h2>
              <div className="max-w-2xl mx-auto space-y-2 sm:space-y-3 px-2">
                <p className="text-base sm:text-lg text-[#d4d4d4] font-semibold">
                  Choose a maximum of 3 visual attributes that best represent your brand
                </p>
                <p className="text-sm sm:text-base text-[#a3a3a3]">
                  Tap any option to select it. Tap again to deselect.
                  Once you select 3 options, other questions will be disabled.
                </p>
              </div>
              <div className="mt-6">
                <div className="inline-flex items-center gap-3 px-6 py-3 bg-teal-50 border-2 border-teal-300 rounded-xl">
                  <span className="text-2xl font-bold text-teal-600">
                    {visualAnswers.length} / {maxVisualQuestions}
                  </span>
                  <span className="text-sm font-medium text-teal-700">
                    Attributes Selected
                  </span>
                </div>
              </div>
            </div>

            {/* All Questions Displayed at Once */}
            <div className="space-y-6 sm:space-y-8 mb-8 sm:mb-10">
              {visualAttributeQuestions.map((question, index) => {
                const isDisabled = visualAnswers.length >= maxVisualQuestions && !isQuestionAnswered(question.id);
                const isOption1Selected = isOptionSelected(question.id, 'option1');
                const isOption2Selected = isOptionSelected(question.id, 'option2');

                return (
                  <div
                    key={question.id}
                    className={`transition-all ${isDisabled ? 'opacity-40' : 'opacity-100'}`}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
                      {/* Gradient Circle Number - 50% larger */}
                      <div 
                        className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-md"
                        style={{ background: 'linear-gradient(135deg, #7A00EB, #00c3ef)' }}
                      >
                        <span className="text-base sm:text-lg font-bold text-white">{question.id}</span>
                      </div>
                      
                      <div className="flex-1 w-full flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                        <button
                          onClick={() => handleVisualAnswer(question.id, 'option1')}
                          disabled={isDisabled}
                          className={`flex-1 p-4 sm:p-5 rounded-lg sm:rounded-xl border-2 font-medium text-left transition-all active:scale-95
                            ${isOption1Selected 
                              ? 'bg-gradient-to-br from-teal-100 to-teal-200 border-teal-400 text-teal-900 shadow-md' 
                              : 'bg-[#0a0a0a] border-[#262626] text-[#d4d4d4] active:border-teal-300'
                            }
                            ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                          `}
                        >
                          <div className="text-base sm:text-lg md:text-xl">{question.option1}</div>
                        </button>

                        <div className="text-gray-400 font-light text-lg sm:text-xl text-center sm:text-left py-1 sm:py-0">or</div>

                        <button
                          onClick={() => handleVisualAnswer(question.id, 'option2')}
                          disabled={isDisabled}
                          className={`flex-1 p-4 sm:p-5 rounded-lg sm:rounded-xl border-2 font-medium text-left transition-all active:scale-95
                            ${isOption2Selected 
                              ? 'bg-gradient-to-br from-teal-100 to-teal-200 border-teal-400 text-teal-900 shadow-md' 
                              : 'bg-[#0a0a0a] border-[#262626] text-[#d4d4d4] active:border-teal-300'
                            }
                            ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                          `}
                        >
                          <div className="text-base sm:text-lg md:text-xl">{question.option2}</div>
                        </button>
                      </div>
                    </div>
                    
                    {/* Separator line between attribute pairs */}
                    {index < visualAttributeQuestions.length - 1 && (
                      <div className="mt-6 sm:mt-8 border-b border-[#262626]"></div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-center gap-4 pt-6 border-t border-[#262626]">
              <Button variant="outline" onClick={goBack} size="lg" className="h-14 sm:h-16 px-6">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to Section 2
              </Button>
              <Button 
                onClick={proceedToEmailSection}
                disabled={visualAnswers.length === 0}
                size="lg"
                style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }}
                className="text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed h-14 sm:h-16 px-6"
              >
                Continue to Final Step
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Section 4: Email Collection with OTP Verification */}
        {section === 4 && (
          <div className="p-2 sm:p-4">
            <div className="text-center mb-6 sm:mb-8">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #7A00EB, #00c3ef)' }}>
                {isVerified ? <Check className="h-6 w-6 sm:h-8 sm:w-8 text-white" /> : <Mail className="h-6 w-6 sm:h-8 sm:w-8 text-white" />}
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#f5f5f5] mb-3 sm:mb-4 px-2">
                {isVerified ? 'Email Verified!' : 'Almost Done!'}
              </h2>
              <p className="text-sm sm:text-base text-[#a3a3a3] px-2">
                {isVerified 
                  ? 'Your email has been verified. Complete your details to see your results.'
                  : 'Enter your details and verify your email to receive your personalized brand archetype results'
                }
              </p>
            </div>

            <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-5 sm:space-y-6">
              <div>
                <Label htmlFor="name" className="text-[#d4d4d4] font-medium mb-2 block">
                  Your Name *
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full h-12 text-base"
                  required
                  data-testid="name-input"
                />
              </div>

              <div>
                <Label htmlFor="companyName" className="text-[#d4d4d4] font-medium mb-2 block">
                  Brand / Company Name *
                </Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder="Your Brand Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full h-12 text-base"
                  required
                  data-testid="company-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-[#d4d4d4] font-medium mb-2 block">
                  Email Address *
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      // Reset OTP state when email changes
                      if (otpSent) {
                        setOtpSent(false);
                        setOtpCode('');
                        setIsVerified(false);
                      }
                    }}
                    className="flex-1 h-12 text-base"
                    required
                    disabled={isVerified}
                    data-testid="email-input"
                  />
                  {!isVerified && (
                    <Button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={isSendingOtp || !email || !email.includes('@')}
                      className="h-12 px-4"
                      style={{ background: otpSent ? '#22c55e' : 'linear-gradient(to right, #7A00EB, #00c3ef)' }}
                      data-testid="send-otp-btn"
                    >
                      {isSendingOtp ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : otpSent ? (
                        'Sent!'
                      ) : (
                        'Send OTP'
                      )}
                    </Button>
                  )}
                  {isVerified && (
                    <div className="h-12 px-4 flex items-center bg-green-100 text-green-700 rounded-md font-medium">
                      <Check className="h-4 w-4 mr-1" /> Verified
                    </div>
                  )}
                </div>
              </div>

              {/* OTP Input Section */}
              {otpSent && !isVerified && (
                <div className="bg-[#f9fafb] dark:bg-[#111] border-2 border-[#a78bfa] rounded-lg p-4 space-y-4" data-testid="otp-section">
                  <div>
                    <Label htmlFor="otp" className="text-[#374151] dark:text-[#e5e7eb] font-medium mb-2 block">
                      Enter 6-digit OTP sent to {email}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="otp"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="000000"
                        value={otpCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtpCode(value);
                        }}
                        className="flex-1 h-12 text-base text-center tracking-[0.5em] font-mono bg-[#ffffff] dark:bg-[#0a0a0a] text-[#111827] dark:text-[#ffffff] border-2 border-[#d1d5db] dark:border-[#525252]"
                        maxLength={6}
                        data-testid="otp-input"
                      />
                      <Button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={isVerifyingOtp || otpCode.length !== 6}
                        className="h-12 px-6 text-white"
                        style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }}
                        data-testid="verify-otp-btn"
                      >
                        {isVerifyingOtp ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Verify'
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={isSendingOtp}
                      className="text-sm text-[#7A00EB] dark:text-[#c4a3ff] hover:underline underline-offset-2"
                      data-testid="resend-otp-btn"
                    >
                      Didn&apos;t receive? Resend OTP
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-row justify-center gap-3 sm:gap-4 pt-4">
                <Button type="button" variant="outline" onClick={goBack} className="h-14 sm:h-16 px-6 min-w-[120px]">
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={!isVerified || isSubmitting}
                  style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }}
                  className="text-white hover:opacity-90 h-14 sm:h-16 px-6 font-semibold disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
                  data-testid="view-results-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : isVerified ? 'View My Results' : 'Verify Email First'}
                </Button>
              </div>
            </form>
          </div>
        )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Quiz;